import * as d3 from 'd3';
import { throttle, normalize, lerp, calculateDistance } from '../utils';
import axios from 'axios';
import Selector from '../components/Selector';
import Observable from '../utils/Observable';
import Sidebar from '../components/Sidebar';
import uniTemplate from '../templates/uniTemplate';


class Map {
  constructor(el, eventBus) {
    console.log('Map');
    this.observable = new Observable();
    this.container = el;
    this.body = document.body;
    this.mapLocations = [];
    this.width = this.container.offsetWidth;
    this.height = this.container.offsetHeight;
    this.eventBus = eventBus || {};
    this.minContainerHeight = 0;
    this.maxContainerHeight = 2000;
    this.maxScale = 120;
    this.zoomButtons = document.querySelectorAll('.map-zoom');
    this.resetButton = document.querySelector('.map-reset');
    this.selector = new Selector(document.querySelector('.unis-select'), this.observable, 'location-selected');
    this.sidebar = new Sidebar(this.observable, uniTemplate);

    this.defs;
    this.filter;
    this.countriesGeo;
    this.centered;
    this.projection;
    this.currentLocation;
    this.scaleRadius;
    this.g;
    this.path;
    this.active;
    this.mousePos;
    this.zoomScale;
    this.minDataVal;
    this.maxDataVal;

    this.svg = d3.select(this.container)
      .append('svg')
      .attr('class', 'svg')
      .attr('preserveAspectRatio', 'xMidYMid')
      .attr('xmlns', 'http://www.w3.org/2000/svg')
      .attr('xlink', 'http://www.w3.org/1999/xlink')
      .attr('version', '1.1');

    this.resize = this.resize.bind(this);
    this.clicked = this.clicked.bind(this);
    this.zoomed = this.zoomed.bind(this);
    this.reset = this.reset.bind(this);
    this.handleZoomClick = this.handleZoomClick.bind(this);
    this.mousedrag = this.mousedrag.bind(this);
    this.mouseup = this.mouseup.bind(this);
    this.zoomStart = this.zoomStart.bind(this);
    this.zoomEnd = this.zoomEnd.bind(this);
    this.handleBubbleOver = this.handleBubbleOver.bind(this);
    this.handleBubbleOut = this.handleBubbleOut.bind(this);
    this.selectLocation = this.selectLocation.bind(this);

    this.zoom = d3.zoom()
      .scaleExtent([1, this.maxScale])
      .translateExtent([[-100, -100], [this.width, this.height]])
      .on('start', this.zoomStart)
      .on('end', this.zoomEnd)
      .on('zoom', this.zoomed);

    this.observable.subscribe('sidebar-closed', this.reset);
    this.observable.subscribe('location-selected', this.selectLocation);
  }

  init() {
    axios.all([this.getGeoJson(), this.getLocations()])
      .then(axios.spread((countriesGeo, mapLocations) => {
        this.countriesGeo = countriesGeo.data;
        this.mapLocations = this.addDistance(mapLocations.data.data);

        // console.log('this.countriesGeo', this.countriesGeo);
        // console.log('this.mapLocations', this.mapLocations);

        const dict = {
          value: 'id',
          text: 'university'
        };

        this.selector.init(this.mapLocations, dict);
        this.selector.reset(); // first item is selected

        this.draw();
        this.bindEventListeners();
        this.container.classList.add('is-visible');
        document.body.classList.add('is-map-visible');
      }))
      .catch(err => {
        console.log('err', err);
      });
  }

  formatData(data) {
    return data.map((d, i) => {
      return {
        id: i,
        ...d
      }
    });
  }

  sanitizeLocationData(data) {
    return data.map((d, i) => {
      // remove all text 
      const s = parseInt(d.totalOffences.toString().replace(/[^0-9a-z]/gi, ''));
      d.totalOffences = isNaN(s) ? -1 : s;
      
      return d;
    });
  }

  bindEventListeners() {
    window.addEventListener('resize', throttle(this.resize), 1000);
    this.resetButton.addEventListener('click', this.reset);
    
    for (let i = 0, len = this.zoomButtons.length; i < len; i++) {
      this.zoomButtons[i].addEventListener('click', this.handleZoomClick);
    }
  }

  getGeoJson() {
    return axios.get('/assets/geojson/uk_geojson_clean.json');
  }

  getLocations() {
    // return axios.get('/assets/data/uni-data_v2.json');
    return axios.get('/api/universities');
  }

  draw() {
    this.drawMap();
    this.drawBubbles();
  }

  drawMap() {
    // create a unit projection
    this.projection = d3.geoMercator()
      .scale(1)
      .translate([0,0]);

    // create a path generator
    this.path = d3.geoPath()
      .projection(this.projection);

    // compute bounds of a point of interest, then derive scale and translate
    // use countriesGeo to center map on england, wales and northern ireland
    let b = this.path.bounds(this.countriesGeo),
      s = .95 / Math.max((b[1][0] - b[0][0]) / this.width, (b[1][1] - b[0][1]) / this.height),
      t = [(this.width - s * (b[1][0] + b[0][0])) / 2, (this.height - s * (b[1][1] + b[0][1])) / 2];

    // update the projection to use computed scale and translate....
    this.projection
      .scale(s)
      .translate(t);

    // remove if group already exists
    this.g = d3.select('.svg g');    
    if (this.g) this.g.remove();

    // set svg dimensions
    this.svg.attr('width', this.width)
      .attr('height', this.height)
      .attr('viewBox', `0 0 ${this.width} ${this.height}`);

    // defs
    this.defs = this.svg.append('defs');
    this.filter = this.defs.append('filter')
      .attr('id', 'drop-shadow')
      .attr('x', '-20%')
      .attr('y', '-20%')
      .attr('height', '180%')
      .attr('width', '180%');

    this.filter.append('feDropShadow')
      .attr('stdDeviation', 4);

    // create group
    this.g = this.svg.append('g')
      .attr('class', 'uk');

    // draw the svg of both the geojson and bounding box
    this.g.selectAll('path')
      .data(this.countriesGeo.features)
      .enter()
      .append('path')
      .attr('d', this.path);

    this.zoomScale = 1;

    this.svg.call(this.zoom);
  }

  zoomStart(e, i, n) {
    // https://stackoverflow.com/questions/38587326/d3-v4-d3-zoom-prevents-binding-some-events-except-on-the-window
    this.mousePos = d3.mouse(n[0]);
    // console.log('this.mousePos', this.mousePos);
    // console.log('mouseDown');
  }

  zoomEnd(e, i, n) {
    const m = d3.mouse(n[0]);
    if (m[0] && m[1]) {
      // console.log('m',m);

      if (this.mousePos[0] === m[0] && this.mousePos[1] === m[1]) {
        // console.log('mouseup!'); 
      }
    }
  }

  zoomed() {
    this.zoomScale = d3.event.transform.k;
    
    this.g.attr('transform', d3.event.transform);
    this.g.selectAll('path')
      .attr('d', this.path.projection(this.projection));

    this.svg.selectAll('circle')
      .style('stroke-width', 1 / d3.event.transform.k)
      .attr('r', (d) => {
        return this.scaleRadius(d.totalOffences) / d3.event.transform.k;
      });
  }

  drawBubbles() {
    this.minDataVal = d3.min(this.mapLocations, (d) => d.totalOffences);
    this.maxDataVal = d3.max(this.mapLocations, (d) => d.totalOffences);

    const n = normalize(this.height, this.minContainerHeight, this.maxContainerHeight);

    const minRange = lerp(2, 6, n);
    const maxRange = lerp(16, 48, n);

    this.scaleRadius = d3.scaleLinear()
      .domain([0, this.maxDataVal])
      .range([minRange, maxRange]);

    // remove bubbles if already exist
    this.bubbles = d3.selectAll('.bubble');
    if (this.bubbles) this.bubbles.remove();

    this.g.selectAll('.bubble')
      .data(this.mapLocations.sort((a, b) => b.totalOffences - a.totalOffences))
      .enter()
      .append('circle')
      .attr('class', (d) => {
        if (!d.providedOffenceData) {
          return `bubble bubble--unknown`;
        }

        if (d.totalOffences === 0) {
          return `bubble bubble--zero`;
        }

        return 'bubble';
      })
      .attr('transform', (d) => {
        const [long, lat] = this.projection([d.longitude, d.latitude]);
        const rad = this.scaleRadius(d.totalOffences);

        return `translate(${long}, ${lat})`;
      })
      .style('stroke-width', 1)
      .attr('r', (d) => {
        return this.scaleRadius(d.totalOffences);
      })
      .on('click', this.clicked)
      .on('mouseover', this.handleBubbleOver)
      .on('mouseout', this.handleBubbleOut);
  }

  clicked(d, i, n) {
    console.log('d', d);

    if (this.active === d) {
      this.selector.reset();
      this.sidebar.close();
      return this.reset();
    }

    const locationInfo = this.getLocationInfo(d.id);
    const [long, lat] = this.projection([d.longitude, d.latitude]);
    this.active = d;

    this.selector.update(d.id);

    let same = (d) => d === this.active;

    // remove all active clases.
    this.g.selectAll('.bubble')
      .classed('active', this.active && same);

    // zoom in on location.
    this.svg.transition()
      .duration(500)
      .ease(d3.easeQuadOut)
      .call(this.zoom.transform,
        d3.zoomIdentity
        .translate(this.width / 2, this.height / 2)
        .scale(120)
        .translate(-long, -lat));

    this.sidebar.update(locationInfo[0]);
    this.sidebar.open();
  }

  mousedrag() {
    // console.log('mouse drag');
    d3.select('body').style('cursor', 'move');
  }

  mouseup() {
    // console.log('mouse up');
    d3.select('body').style('cursor', 'auto');
  }

  handleBubbleOver(d, i, els) {
    const el = els[i];
    const rad = this.scaleRadius(d.totalOffences);
    const newRad = (rad / this.zoomScale) * 2;

    

    el.setAttribute('r', newRad);
  }

  handleBubbleOut(d, i, els) {
    const el = els[i];
    const newRad = (this.scaleRadius(d.totalOffences) / this.zoomScale);

    el.setAttribute('r', newRad);
  }

  reset() {
    // remove all active classes from bubbles
    this.g.selectAll('.bubble')
      .classed('active', false);

    // reset active
    this.active = null;
    // reset select
    this.selector.reset();

    // return map to normal
    this.svg.transition()
      .duration(500)
      .ease(d3.easeQuadOut)
      .call(this.zoom.transform,
        d3.zoomIdentity
        .translate(0,0)
        .scale(1)
      );
  }

  getLocationInfo(id) {
    return this.mapLocations.filter((item) => item.id === id);
  }

  handleZoomClick(e) {
    const el = e.currentTarget;
    const direction = parseInt(el.dataset.zoom, 10) === 1 ? 1.5 : 0.5;

    this.zoom.scaleBy(
      this.svg.transition()
        .duration(500)
        .ease(d3.easeQuadOut), direction
    );
  }

  selectLocation(id) {
    const selected = this.mapLocations.filter((location) => {
      return location.id === parseInt(id);
    });

    this.clicked(selected[0]);
  }

  resize() {
    // only resize on change of width to avoid resize on scroll
    if(this.container.offsetWidth != this.width) {
      this.width = this.container.offsetWidth;
      this.height = this.container.offsetHeight;
      // redraw the svg
      this.draw();
    }
  }

  addDistance(data) {
    const clone = [...data];
    const modData = [...data];

    for (let i = 0, len = clone.length; i < len; i ++) {
      clone[i]['distance'] = calculateDistance(clone[0]['latitude'], clone[0]['longitude'], clone[i]['latitude'], clone[i]['longitude'], 'K');
    }

    const orderedByDistance = clone.sort((a, b) => a.distance - b.distance);

    // add prev and next based on distance
    for (let i = 0, len = orderedByDistance.length; i < len; i ++) {
      // if there is a previous sibling
      orderedByDistance[i]['distancePrevEntry'] = i > 0 ? { id: orderedByDistance[i - 1].id, title: orderedByDistance[i - 1].university } : null;
      // if there is a next sibling
      orderedByDistance[i]['distanceNextEntry'] = i < orderedByDistance.length -1 ? { id: orderedByDistance[i + 1].id, title: orderedByDistance[i + 1].university } : null;
    }

    for (let i = 0, len = clone.length; i < len; i++) {
      let orderedObj = orderedByDistance.find(x => x.id === modData[i].id);

      modData[i] = {
        ...modData[i],
        distance: orderedObj.distance,
        distancePrevEntry: orderedObj.distancePrevEntry,
        distanceNextEntry: orderedObj.distanceNextEntry
      }
    }

    return modData;
  }
}

export default Map;